<template>
  <!--
    The slot payload is `{ field, state }` — the handlers hang off the field API,
    not off the payload. Destructuring `handleChange` from the top level yields
    undefined, Vue drops an undefined listener without a word, and every control
    in the wizard writes into nothing while still looking alive: DS controls hold
    their own value internally, so only a field that cannot (the segmented date)
    shows it. Aliased to `fieldApi` because `field` here is this component's own
    prop — the field definition.
  -->
  <Field
    v-slot="{ field: fieldApi, state }"
    :form
    :name="field.name"
  >
    <!--
      The grid cell is this div — TanStack's Field is renderless and refuses
      attrs, so a `class` on WizardField would go nowhere. The span therefore
      comes off the definition rather than from the caller.
    -->
    <div
      ref="cell"
      class="min-w-0"
      :class="field.span === 2 ? 'sm:col-span-2' : field.span === 3 ? 'lg:col-span-3' : ''"
    >
      <DSFieldLabel
        v-if="!hideLabel"
        :id="labelId"
        class="mb-1.5"
        :for="controlId"
        :label="field.label"
        :mark="markFor(origin)"
      />

      <!--
        Shown rather than offered for editing: the summary reads a value back
        rather than asking for it a second time, and every field it shows this
        way stays editable in the step that owns it. A value the flow fixes
        never reaches here.
      -->
      <p
        v-if="isStatic"
        class="flex items-center gap-1.5 text-base font-medium text-foreground"
      >
        <span class="opacity-50">{{ display(state.value) || '—' }}</span>
      </p>

      <template v-else>
        <WizardControl
          :id="controlId"
          :labelled-by
          :suggestion
          :suggestion-loading
          :askable
          :field
          :value="state.value"
          @change="fieldApi.handleChange"
          @blur="fieldApi.handleBlur"
          @ask="emit('ask')"
        />
        <!--
          Only after the field has been touched: a wizard opened at the summary
          would otherwise greet the user with a column of errors for fields they
          have not reached yet.
        -->
        <p
          v-if="state.meta.isTouched && state.meta.errors.length"
          class="mt-1.5 text-xs font-medium text-destructive"
        >
          {{ errorMessage(state.meta.errors[0]) }}
        </p>
      </template>

      <p
        v-if="field.hint && !readonly"
        class="mt-1.5 text-xs font-medium leading-normal text-muted-foreground"
      >
        {{ field.hint }}
      </p>
    </div>
  </Field>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import { Field } from '@tanstack/vue-form'
import { DSFieldLabel, type ComboboxSuggestion } from '@ds'

import { FRACTION_DIGITS } from '@/const'

import { stringToLocalDateString } from '@/helpers/dates'
import { numberFormat } from '@/helpers/numbers'

import WizardControl from './WizardControl.vue'

import { useWizardFieldFocus } from '../utils/focus'
import { markFor } from '../utils/helpers'
import { WizardFieldDef, WizardFieldOrigin } from '../utils/types'

/*
 * One field of a wizard, bound to its TanStack field by name.
 *
 * The form is passed in rather than injected: a wizard has exactly one form, and
 * a field that could silently bind to a different one is a bug that looks like a
 * rendering glitch. Loosely typed on purpose — the field definitions are data,
 * so the form's value type is not known here; the schema in each wizard folder
 * is where the shape is actually enforced.
 */
const { field, hideLabel, origin, readonly } = defineProps<{
  form: any
  field: WizardFieldDef
  origin: WizardFieldOrigin
  /** Renders as text instead of a control. Set on the summary for filled values. */
  readonly?: boolean
  /** The heading slot draws its own label above the control. */
  hideLabel?: boolean
  /** A suggested row for a combobox field — see WizardControl. */
  suggestion?: ComboboxSuggestion
  /** That row is still being decided — see WizardControl. */
  suggestionLoading?: boolean
  /** Offers the "Ask AI" row in place of a suggestion — see WizardControl. */
  askable?: boolean
}>()

const emit = defineEmits<{
  /** The "Ask AI" row was clicked — see WizardControl. */
  (e: 'ask'): void
}>()

/*
 * The caption and the control are tied together rather than the control naming
 * itself: a visible label that is not the control's label leaves the field with
 * two names, and clicking the caption does nothing. `useId` because the id has
 * to be unique per instance — two wizards on screen would otherwise collide.
 *
 * Undefined when there is nothing to point at — the label is hidden, or the
 * field is a read value rendered as text — which is also what tells
 * WizardControl to name itself with `aria-label` instead.
 *
 * A date field is the exception: it is a group of segments, and `role="group"`
 * is not a labelable element, so a `<label for>` aimed at it does nothing. That
 * one is labelled by reference — hence the label carries an id of its own.
 */
const generatedId = useId()

/*
 * Registered so the missing-fields banner can reach this field by name — the
 * chip knows the name, and only this component knows the element. A read
 * value registers too: it renders as text with nothing to focus, and the
 * registry answering "nothing to focus here" is better than the banner
 * believing the field is on another step.
 */
const cell = ref<HTMLElement>()

useWizardFieldFocus(() => field.name, cell)

/*
 * Rendered as text rather than as a control: a value the summary is reading
 * back. Which values those are is the summary's call — see readonlyFields —
 * and every one of them stays editable in the step that collects it.
 */
const isStatic = computed(() => readonly)

const isLabelled = computed(() => !hideLabel && !isStatic.value)

const isSegmented = computed(() => field.control === 'date')

const labelId = computed(() => (hideLabel ? undefined : `${generatedId}-label`))

const controlId = computed(() =>
  isLabelled.value && !isSegmented.value ? generatedId : undefined,
)

const labelledBy = computed(() =>
  isLabelled.value && isSegmented.value ? labelId.value : undefined,
)

/*
 * A value shown as text goes through the same formatters the rest of the app
 * reads with — an amount grouped, a date in the user's own format. A raw
 * `String(value)` here put `1000000` and an ISO date on the summary, which is
 * the wizard telling the user what it stored rather than what they entered.
 *
 * Minimum zero fraction digits, not the app's usual two: this echoes what was
 * typed, and "1,000,000.00" claims a precision nobody gave.
 */
const display = (value: unknown) => {
  if (value === undefined || value === null || value === '') return ''

  if (field.control === 'number') {
    return numberFormat(String(value), {
      fractionDigits: 0,
      fractionDigitsMax: FRACTION_DIGITS,
    })
  }

  if (field.control === 'date') {
    return stringToLocalDateString(String(value)) ?? String(value)
  }

  return String(value)
}

/**
 * Standard Schema hands back issue objects; a plain-function validator hands
 * back a string. Both reach here, so both are read.
 */
const errorMessage = (error: unknown) => {
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }
  return 'Check this field'
}
</script>
